import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';

interface Document {
  id: number;
  name: string;
  category: string;
  size: string;
  uploadDate: string;
  uploadedBy: string;
  type: 'pdf' | 'doc' | 'xls' | 'img';
  downloads: number;
}

export default function DocumentsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const documents: Document[] = [
    {
      id: 1,
      name: 'HOA Bylaws 2025',
      category: 'Legal',
      size: '2.4 MB',
      uploadDate: '2025-01-05',
      uploadedBy: 'Board Admin',
      type: 'pdf',
      downloads: 156
    },
    {
      id: 2,
      name: 'December 2024 Meeting Minutes',
      category: 'Minutes',
      size: '856 KB',
      uploadDate: '2024-12-20',
      uploadedBy: 'Sarah Johnson',
      type: 'pdf',
      downloads: 89
    },
    {
      id: 3,
      name: 'Community Rules & Regulations',
      category: 'Legal',
      size: '1.8 MB',
      uploadDate: '2024-12-15',
      uploadedBy: 'Board Admin',
      type: 'pdf',
      downloads: 234
    },
    {
      id: 4,
      name: 'Financial Report Q4 2024',
      category: 'Financial',
      size: '3.2 MB',
      uploadDate: '2024-12-30',
      uploadedBy: 'Michael Chen',
      type: 'xls',
      downloads: 67
    },
    {
      id: 5,
      name: 'Pool Maintenance Schedule',
      category: 'Maintenance',
      size: '245 KB',
      uploadDate: '2025-01-02',
      uploadedBy: 'Maintenance Team',
      type: 'pdf',
      downloads: 123
    },
    {
      id: 6,
      name: 'Architectural Guidelines',
      category: 'Guidelines',
      size: '4.1 MB',
      uploadDate: '2024-11-20',
      uploadedBy: 'Board Admin',
      type: 'pdf',
      downloads: 178
    },
    {
      id: 7,
      name: 'Community Map',
      category: 'Reference',
      size: '5.6 MB',
      uploadDate: '2024-10-15',
      uploadedBy: 'Board Admin',
      type: 'img',
      downloads: 201
    },
    {
      id: 8,
      name: 'Emergency Contact List',
      category: 'Reference',
      size: '128 KB',
      uploadDate: '2025-01-01',
      uploadedBy: 'Safety Committee',
      type: 'doc',
      downloads: 145
    }
  ];

  const categories = ['all', 'Legal', 'Minutes', 'Financial', 'Maintenance', 'Guidelines', 'Reference'];

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return 'ri-file-pdf-line text-red-500';
      case 'doc':
        return 'ri-file-word-line text-blue-500';
      case 'xls':
        return 'ri-file-excel-line text-green-500';
      case 'img':
        return 'ri-image-line text-purple-500';
      default:
        return 'ri-file-line text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['Inter',sans-serif]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Document Library</h1>
          <p className="text-lg text-gray-600">
            Access important community documents, bylaws, meeting minutes, and reports
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-3 rounded-xl font-medium whitespace-nowrap cursor-pointer transition-all ${
                    selectedCategory === category
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>

            {/* Upload Button */}
            <button
              onClick={() => setUploadModalOpen(true)}
              className="px-6 py-3 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-upload-cloud-line text-xl"></i>
              Upload
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <i className="ri-file-list-line text-teal-600 text-2xl"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{documents.length}</div>
                <div className="text-sm text-gray-600">Total Documents</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-folder-line text-blue-600 text-2xl"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{categories.length - 1}</div>
                <div className="text-sm text-gray-600">Categories</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-download-line text-purple-600 text-2xl"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">1,193</div>
                <div className="text-sm text-gray-600">Total Downloads</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-green-600 text-2xl"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">3</div>
                <div className="text-sm text-gray-600">Added This Week</div>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((doc) => (
            <div
              key={doc.id}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  doc.type === 'pdf' ? 'bg-red-50' :
                  doc.type === 'doc' ? 'bg-blue-50' :
                  doc.type === 'xls' ? 'bg-green-50' : 'bg-purple-50'
                }`}>
                  <i className={`${getFileIcon(doc.type)} text-2xl`}></i>
                </div>
                <button className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-more-2-fill text-xl"></i>
                </button>
              </div>

              <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{doc.name}</h3>
              
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  {doc.category}
                </span>
                <span className="text-xs text-gray-500">{doc.size}</span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <i className="ri-calendar-line"></i>
                <span>{new Date(doc.uploadDate).toLocaleDateString()}</span>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-download-line"></i>
                  <span>{doc.downloads}</span>
                </div>
                <button className="px-4 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredDocuments.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No documents found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setUploadModalOpen(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Upload Document</h2>
              <button
                onClick={() => setUploadModalOpen(false)}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center mb-6 hover:border-teal-500 transition-colors cursor-pointer">
              <i className="ri-upload-cloud-2-line text-6xl text-gray-400 mb-4"></i>
              <p className="text-lg font-medium text-gray-900 mb-2">
                Drop files here or click to browse
              </p>
              <p className="text-sm text-gray-600">
                Supports PDF, DOC, XLS, and images up to 10MB
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Name
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="Enter document name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm">
                  <option>Legal</option>
                  <option>Minutes</option>
                  <option>Financial</option>
                  <option>Maintenance</option>
                  <option>Guidelines</option>
                  <option>Reference</option>
                </select>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setUploadModalOpen(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                Upload Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
